<template>
  <div class="resident-reports-ant">
    <div v-if="isCompletelyEmpty" class="resident-reports-ant__empty">
      <h3 class="resident-reports-ant__empty-title">Нет отчетов</h3>
      <p class="resident-reports-ant__empty-text">Вы ещё не создали ни одного отчета.</p>

      <button type="button" class="btn btn-primary btn-medium" @click="handleCreateReport">
        <img :src="PLUS_ICON" />
        <span>Новый отчет</span>
      </button>
    </div>

    <a-table
      v-else-if="!hasActiveSearch || filteredReports.length"
      class="resident-reports-ant__table"
      :columns="columns"
      :bordered="true"
      :data-source="filteredReports"
      :loading="loading"
      :pagination="false"
      :scroll="TABLE_SCROLL"
      :custom-row="getRowProps"
      row-key="id"
    >
      <template #bodyCell="{ column, record, index }">
        <template v-if="column.key === 'number'"> {{ index + 1 }}. </template>

        <template v-else-if="column.key === 'year'"> {{ formatReportYear(record.year) }} </template>

        <template v-else-if="column.key === 'createdAt'">
          {{ formatDate(record.createdAt) }}
        </template>

        <template v-else-if="column.key === 'status'">
          <span
            :class="[
              'resident-reports-ant__status',
              `resident-reports-ant__status--${getStatusModifier(record.status)}`,
            ]"
          >
            <span class="resident-reports-ant__status-dot" aria-hidden="true"></span>
            {{ record.status || DEFAULT_STATUS }}
          </span>
        </template>

        <template v-else-if="column.key === 'signature'">
          <a-popover
            v-if="isReportSigned(record)"
            overlay-class-name="resident-reports-ant__signature-popover"
            placement="bottomLeft"
            trigger="hover"
            :mouse-enter-delay="0.2"
            :get-popup-container="getSignaturePopoverContainer"
          >
            <template #content>
              <dl class="resident-reports-ant__signature-tooltip">
                <div
                  v-for="item in getSignatureTooltipItems(record)"
                  :key="item.label"
                  class="resident-reports-ant__signature-tooltip-item"
                >
                  <dt>{{ item.label }}</dt>
                  <dd>{{ item.value }}</dd>
                </div>
              </dl>
            </template>

            <span
              class="resident-reports-ant__signature resident-reports-ant__signature--signed resident-reports-ant__signature--interactive"
              @click.stop
              @mouseenter.stop
            >
              <img :src="SIGNED_ICON" alt="" aria-hidden="true" />
              Подписан
            </span>
          </a-popover>

          <span
            v-else
            class="resident-reports-ant__signature resident-reports-ant__signature--unsigned"
          >
            <img :src="NOT_SIGNED_ICON" alt="" aria-hidden="true" />
            Не подписан
          </span>
        </template>

        <template v-else-if="column.key === 'actions'">
          <a-dropdown :trigger="['click']">
            <button
              class="resident-reports-ant__actions-btn"
              type="button"
              aria-label="Действия с отчётом"
              @click.stop
            >
              <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
            </button>
            <template #overlay>
              <a-menu class="resident-reports-ant__actions-menu" @click.stop>
                <a-menu-item key="edit" @click.stop="handleEditReport(record.id)">
                  Редактировать
                </a-menu-item>
                <a-menu-item key="download">
                  <a
                    :href="getDownloadHref(record.id)"
                    target="_blank"
                    rel="noopener"
                    class="resident-reports-ant__actions-menu--link"
                  >
                    Скачать <span class="resident-reports-ant__actions-menu--zip">(.zip)</span>
                  </a>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </template>
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import type { TableColumnsType } from 'ant-design-vue';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import { initSearchInput, querySearchInput } from '@/js/utils/search-input';
import { getReportDetailHref } from '../resident-report-detail';
import { openResidentReportDrawer } from '../resident-report-drawer';
import {
  fetchReportDetail,
  fetchReportList,
  getReportArchiveUrl,
} from '../resident-report/report-api';
import { mapApiReportToEditPayload } from '../resident-report-detail/map-report-detail';

interface ReportListItem {
  id: number;
  createdAt: string;
  year: number;
  status?: string | null;
  isSigned?: boolean;
  fioSignatory?: string | null;
  certNumber?: string | null;
  dateSign?: string | null;
  nameOrganization?: string | null;
  certValidUntil?: string | null;
}

const DEFAULT_STATUS = 'Черновик';
const SEARCH_INPUT_ID = 'residentReportsSearch';
const TABLE_SCROLL = { x: 720, y: 500 };
const SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/signed.svg');
const NOT_SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/not-signed.svg');
const PLUS_ICON = getAssetsPath('/assets/images/icons/plus-icon.svg');

const columns: TableColumnsType<ReportListItem> = [
  {
    title: '#',
    key: 'number',
    width: 50,
  },
  {
    title: 'Отчётный период',
    key: 'year',
    dataIndex: 'year',
    width: 140,
  },
  {
    title: 'Дата подачи',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 130,
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 200,
    customCell: () => ({
      class: 'resident-reports-ant__status-cell',
    }),
  },
  {
    title: 'Эл. подпись',
    key: 'signature',
    dataIndex: 'isSigned',
    width: 150,
  },
  {
    title: '',
    key: 'actions',
    width: 48,
    align: 'right',
  },
];

const mockReports: ReportListItem[] = [
  { id: 1, createdAt: '2026-05-03T14:32:00', year: 2026, status: 'Черновик', isSigned: false },
  { id: 2, createdAt: '2025-05-03T10:11:00', year: 2025, status: 'Принят', isSigned: false },
  {
    id: 3,
    createdAt: '2024-05-03T12:00:00',
    year: 2024,
    status: 'На рассмотрении',
    isSigned: true,
    dateSign: '2026-05-03T12:00:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T12:00:00',
  },
  {
    id: 4,
    createdAt: '2023-05-03T09:40:00',
    year: 2023,
    status: 'Требует доработки',
    isSigned: false,
  },
  {
    id: 5,
    createdAt: '2022-05-03T16:20:00',
    year: 2022,
    status: 'Принят',
    isSigned: true,
    dateSign: '2026-05-03T16:20:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T16:20:00',
  },
  {
    id: 6,
    createdAt: '2021-05-03T11:25:00',
    year: 2021,
    status: 'Принят',
    isSigned: true,
    dateSign: '2026-05-03T11:25:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T11:25:00',
  },
  {
    id: 7,
    createdAt: '2020-05-03T13:10:00',
    year: 2020,
    status: 'Принят',
    isSigned: true,
    dateSign: '2026-05-03T13:10:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T13:10:00',
  },
  { id: 8, createdAt: '2019-05-03T15:45:00', year: 2019, status: 'Принят', isSigned: false },
  {
    id: 9,
    createdAt: '2018-05-03T10:30:00',
    year: 2018,
    status: 'Принят',
    isSigned: true,
    dateSign: '2026-05-03T10:30:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T10:30:00',
  },
  {
    id: 10,
    createdAt: '2017-05-03T09:15:00',
    year: 2017,
    status: 'Принят',
    isSigned: true,
    dateSign: '2026-05-03T09:15:00',
    certNumber: '098.765.432.1',
    fioSignatory: 'Константинов Е. П.',
    nameOrganization: 'ООО «Компания_название»',
    certValidUntil: '2026-05-03T09:15:00',
  },
];

const loading = ref(false);
const reports = ref<ReportListItem[]>([]);
const searchQuery = ref('');
let cleanupSearchInput: (() => void) | null = null;
let cleanupReportsReload: (() => void) | null = null;

const filteredReports = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (!query) return reports.value;

  return reports.value.filter((report) => {
    const values = [
      formatReportYear(report.year),
      formatDate(report.createdAt),
      report.status || DEFAULT_STATUS,
      report.isSigned ? 'подписан' : 'не подписан',
    ];

    return values.some((value) => value.toLowerCase().includes(query));
  });
});

const isCompletelyEmpty = computed(
  () => !loading.value && reports.value.length === 0 && !searchQuery.value.trim(),
);

const hasActiveSearch = computed(() => Boolean(searchQuery.value.trim()));

function updateSearchSummary(): void {
  const summary = querySearchInput(document, SEARCH_INPUT_ID)?.summary;
  if (!summary) return;

  const query = searchQuery.value.trim();
  if (!query) {
    summary.hidden = true;
    summary.innerHTML = '';
    return;
  }

  summary.hidden = false;
  summary.innerHTML = formatMatchCountSummary(filteredReports.value.length, query);
}

function syncPageState(): void {
  const page = document.querySelector<HTMLElement>('.resident-reports-page');
  if (!page) return;

  page.classList.toggle('resident-reports-page--empty', isCompletelyEmpty.value);
}

watch([searchQuery, filteredReports], () => {
  updateSearchSummary();
});

function handleCreateReport(): void {
  openResidentReportDrawer('create');
}
onMounted(() => {
  bindSearchInput();
  bindReportsReload();
  loadReports();
});

onBeforeUnmount(() => {
  cleanupSearchInput?.();
  cleanupReportsReload?.();
});

async function loadReports(): Promise<void> {
  loading.value = true;

  try {
    const data = (await fetchReportList()) as ReportListItem[];
    reports.value = data.map(normalizeReportListItem);
  } catch {
    reports.value = mockReports.map(normalizeReportListItem);
  } finally {
    loading.value = false;
    syncPageState();
    updateSearchSummary();
  }
}

function normalizeReportListItem(item: ReportListItem): ReportListItem {
  return {
    ...item,
    isSigned: normalizeIsSigned(item.isSigned),
  };
}

function normalizeIsSigned(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'y';
  }

  return false;
}

function isReportSigned(record: ReportListItem): boolean {
  return normalizeIsSigned(record.isSigned);
}

function getSignaturePopoverContainer(): HTMLElement {
  return document.body;
}

function bindSearchInput(): void {
  const elements = initSearchInput(document, {
    inputId: SEARCH_INPUT_ID,
    mode: 'server',
  });

  if (!elements) {
    return;
  }

  const syncSearchQuery = (): void => {
    searchQuery.value = elements.input.value;
    syncPageState();
    updateSearchSummary();
  };

  syncSearchQuery();
  elements.input.addEventListener('input', syncSearchQuery);
  elements.clearBtn.addEventListener('click', syncSearchQuery);

  cleanupSearchInput = () => {
    elements.input.removeEventListener('input', syncSearchQuery);
    elements.clearBtn.removeEventListener('click', syncSearchQuery);
    cleanupSearchInput = null;
  };
}

function bindReportsReload(): void {
  const reloadReports = (): void => {
    loadReports();
  };

  window.addEventListener('residentReports:changed', reloadReports);
  cleanupReportsReload = () => {
    window.removeEventListener('residentReports:changed', reloadReports);
    cleanupReportsReload = null;
  };
}

function getRowProps(record: ReportListItem): { onClick: () => void } {
  return {
    onClick: () => {
      window.location.href = getDetailHref(record.id);
    },
  };
}

async function handleEditReport(id: number): Promise<void> {
  try {
    const data = await fetchReportDetail(id);
    openResidentReportDrawer('edit', mapApiReportToEditPayload(id, data));
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Не удалось открыть отчёт для редактирования',
    );
  }
}

function getDetailHref(id: number): string {
  return getReportDetailHref(id);
}

function getDownloadHref(id: number): string {
  return getReportArchiveUrl(id);
}

function getSignatureTooltipItems(record: ReportListItem): Array<{ label: string; value: string }> {
  return [
    {
      label: 'Дата подписи',
      value: formatDate(record.dateSign || record.createdAt),
    },
    {
      label: 'Сертификат, №',
      value: record.certNumber || 'Не указан',
    },
    {
      label: 'Владелец',
      value: record.fioSignatory || 'Не указан',
    },
    {
      label: 'Компания',
      value: record.nameOrganization || 'Не указана',
    },
    {
      label: 'Срок действия сертификата',
      value: formatDate(record.certValidUntil || record.dateSign || record.createdAt),
    },
  ];
}

function formatDate(iso: string): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleDateString('ru-RU');
}

function formatReportYear(year: number): string {
  return year ? `${year} г.` : '';
}

function getStatusModifier(status?: string | null): string {
  const normalized = (status || DEFAULT_STATUS).toLowerCase();

  if (normalized.includes('принят')) return 'accepted';
  if (normalized.includes('доработ')) return 'revision';
  if (normalized.includes('рассмотр')) return 'review';

  return 'draft';
}
</script>
